/**
 * Pure utility functions for CV PDF rendering.
 * Extracted into a separate module to enable unit testing
 * without pulling in @react-pdf/renderer dependencies.
 */

import { formatContactDisplay } from './cvDefaults';
import { CVFontFamily, CVPersonalInfo } from '../types';

export const FONT_SIZE_MAP = {
  small: { name: 18, title: 10, body: 8.5, section: 11, gdpr: 6.5 },
  medium: { name: 20, title: 11, body: 9.5, section: 12, gdpr: 7 },
  large: { name: 24, title: 13, body: 10.5, section: 14, gdpr: 7.5 },
};

export type FontSizePreset = keyof typeof FONT_SIZE_MAP;
export type FontSizes = (typeof FONT_SIZE_MAP)[FontSizePreset];

export const SIDEBAR_SECTION_TYPES = ['skills', 'languages', 'certifications', 'hobbies', 'interests'];

export const REGISTERED_FONT_FAMILIES: CVFontFamily[] = ['Inter', 'Poppins', 'Lato', 'Roboto', 'Merriweather'];

export type DescriptionSegment =
  | { type: 'bullet'; text: string }
  | { type: 'text'; text: string }
  | { type: 'break' };

/**
 * Splits a description string into an ordered list of segments.
 * - Lines starting with "•" or "- " become bullet segments.
 * - Other non-empty lines become text segments.
 * - Empty lines become break segments (preserved spacing).
 * The order of segments matches the original input.
 */
export const parseDescription = (description: string): DescriptionSegment[] => {
  const lines = description.split('\n');
  const segments: DescriptionSegment[] = [];

  for (const rawLine of lines) {
    const line = rawLine.trim();

    if (!line) {
      // Only add a break if there's already content (avoid leading breaks)
      if (segments.length > 0) {
        segments.push({ type: 'break' });
      }

      continue;
    }

    if (line.startsWith('•') || line.startsWith('- ')) {
      segments.push({ type: 'bullet', text: line.replace(/^[•\-]\s*/, '') });
    } else {
      segments.push({ type: 'text', text: line });
    }
  }

  // Trim trailing breaks
  while (segments.length > 0 && segments[segments.length - 1].type === 'break') {
    segments.pop();
  }

  return segments;
};

/**
 * Filters out entries with empty titles from a section.
 * Prevents empty placeholder rows from showing in preview/PDF.
 */
export const filterEmptyEntries = <T extends { title: string }>(entries: T[]): T[] =>
  entries.filter((e) => e.title.trim());

/**
 * Returns a Set of lowercased titles that appear more than once in the entries.
 * Used to highlight duplicate skills/hobbies/interests in the preview.
 */
export const getDuplicateTitles = (entries: { title: string }[]): Set<string> => {
  const seen = new Set<string>();
  const dupes = new Set<string>();

  for (const entry of entries) {
    const key = entry.title.trim().toLowerCase();
    if (!key) continue;
    if (seen.has(key)) dupes.add(key);
    seen.add(key);
  }

  return dupes;
};

/**
 * Builds the contact items array from personal info for PDF rendering.
 * Filters empty values and formats URLs for display.
 */
export const buildContactItems = (personalInfo: CVPersonalInfo): string[] => {
  return [
    personalInfo.email,
    personalInfo.phone,
    personalInfo.location,
    personalInfo.linkedin,
    personalInfo.github,
    personalInfo.website,
  ].filter(Boolean).map(formatContactDisplay);
};

export interface ContactLink {
  display: string;
  href: string | null;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[+\d(][\d\s\-().]+$/;

/**
 * Resolves a contact value into a display string + clickable href.
 * - Email → mailto:
 * - Phone → tel:
 * - URL-like → https:// (adds protocol if missing)
 * - Location/plain text → no link
 */
export const resolveContactLink = (value: string): ContactLink => {
  const display = formatContactDisplay(value);

  if (EMAIL_REGEX.test(value)) {
    return { display, href: `mailto:${value}` };
  }
  if (PHONE_REGEX.test(value.replace(/\s/g, ''))) {
    return { display, href: `tel:${value.replace(/[\s\-()]/g, '')}` };
  }
  if (value.startsWith('http://') || value.startsWith('https://')) {
    return { display, href: value };
  }
  // Looks like a URL (contains dot + no spaces) → add https
  if (value.includes('.') && !value.includes(' ')) {
    return { display, href: `https://${value}` };
  }
  // Plain text (location, etc.)
  return { display, href: null };
};

/**
 * Builds contact items with link resolution for PDF rendering.
 */
export const buildContactLinks = (personalInfo: CVPersonalInfo): ContactLink[] => {
  return [
    personalInfo.email,
    personalInfo.phone,
    personalInfo.location,
    personalInfo.linkedin,
    personalInfo.github,
    personalInfo.website,
  ].filter(Boolean).map(resolveContactLink);
};

/**
 * Resolves a URL string into a clickable href (adds https:// if needed).
 * Returns null for empty values.
 */
export const resolveUrlHref = (url: string): string | null => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `https://${url}`;
};
